import { Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { crudGuard, detailGuard } from '~ngx-shared/guards';
import { breadcrumbResolver, crudResolver } from '~ngx-shared/resolvers';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/layout/layout.component').then(m => m.LayoutComponent),
    providers: [provideTranslocoScope('shared'), provideTranslocoScope('finance')],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      /******************
       * HOME
       ******************/
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard-page/dashboard-page.component').then(
            m => m.DashboardPageComponent
          ),
        data: {
          breadcrumb: 'dashboard'
        }
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile-page/profile-page.component').then(m => m.ProfilePageComponent),
        data: {
          breadcrumb: 'profile'
        }
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings-page/settings-page.component').then(
            m => m.SettingsPageComponent
          ),
        data: {
          breadcrumb: 'settings'
        }
      },
      /******************
       * FINANCE
       ******************/
      {
        path: 'account-plan',
        data: {
          breadcrumb: 'finance.account_plan'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/account-plan-overview-page/account-plan-overview-page.component').then(
                m => m.AccountPlanOverviewPageComponent
              )
          },
          {
            path: 'edit',
            loadComponent: () =>
              import('./pages/account-plan-page/account-plan-page.component').then(
                m => m.AccountPlanPageComponent
              ),
            data: {
              breadcrumb: 'edit_value',
              breadcrumbParamValue: 'finance.account_plan',
              icon: 'pi pi-fw pi-money-bill'
            }
          },
          {
            path: 'account-groups',
            data: {
              breadcrumb: 'finance.account_groups',
              icon: 'pi pi-fw pi-money-bill'
            },
            children: [
              {
                path: '',
                redirectTo: '/account-plan',
                pathMatch: 'full'
              },
              {
                path: 'detail/:account-group-id',
                loadComponent: () =>
                  import('./pages/account-group-detail-page/account-group-detail-page.component').then(
                    m => m.AccountGroupDetailPageComponent
                  ),
                canActivate: [detailGuard('account-group-id')],
                resolve: { crud: crudResolver('account-group-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'finance.account_group'
                }
              },
              {
                path: ':account-group-id',
                loadComponent: () =>
                  import('./pages/create-update-account-group-form-page/create-update-account-group-form-page.component').then(
                    m => m.CreateUpdateAccountGroupFormPageComponent
                  ),
                canActivate: [crudGuard('account-group-id')],
                resolve: {
                  crud: crudResolver('account-group-id'),
                  breadcrumb: breadcrumbResolver('account-group-id')
                },
                data: {
                  breadcrumbParamValue: 'finance.account_group'
                }
              }
            ]
          },
          {
            path: 'accounts',
            data: {
              breadcrumb: 'finance.account',
              icon: 'pi pi-fw pi-money-bill'
            },
            children: [
              {
                path: '',
                redirectTo: '/account-plan',
                pathMatch: 'full'
              },
              {
                path: 'detail/:account-id',
                loadComponent: () =>
                  import('./pages/account-detail-page/account-detail-page.component').then(
                    m => m.AccountDetailPageComponent
                  ),
                canActivate: [detailGuard('account-id')],
                resolve: { crud: crudResolver('account-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'finance.account'
                }
              },
              {
                path: ':account-id',
                loadComponent: () =>
                  import('./pages/create-update-account-form-page/create-update-account-form-page.component').then(
                    m => m.CreateUpdateAccountFormPageComponent
                  ),
                canActivate: [crudGuard('account-id')],
                resolve: {
                  crud: crudResolver('account-id'),
                  breadcrumb: breadcrumbResolver('account-id')
                },
                data: {
                  breadcrumbParamValue: 'finance.account'
                }
              }
            ]
          }
        ]
      },
      {
        path: 'entries',
        data: {
          breadcrumb: 'finance.entries'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/entry-list-page/entry-list-page.component').then(
                m => m.EntryListPageComponent
              )
          },
          {
            path: 'detail/:entry-number',
            loadComponent: () =>
              import('./pages/entry-detail-page/entry-detail-page.component').then(
                m => m.EntryDetailPageComponent
              ),
            canActivate: [detailGuard('entry-number')],
            resolve: { crud: crudResolver('entry-number', 'number') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'finance.entry'
            }
          },
          {
            path: 'detail/id/:entry-id',
            loadComponent: () =>
              import('./pages/entry-detail-page/entry-detail-page.component').then(
                m => m.EntryDetailPageComponent
              ),
            canActivate: [detailGuard('entry-id')],
            resolve: { crud: crudResolver('entry-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'finance.entry'
            }
          },
          {
            path: ':entry-id',
            loadComponent: () =>
              import('./pages/create-entry-form-page/create-entry-form-page.component').then(
                m => m.CreateEntryFormPageComponent
              ),
            canActivate: [crudGuard('entry-id', true, false)],
            resolve: {
              crud: crudResolver('entry-id'),
              breadcrumb: breadcrumbResolver('entry-id')
            },
            data: {
              breadcrumbParamValue: 'finance.entry'
            }
          }
        ]
      },
      {
        path: 'bills',
        data: {
          breadcrumb: 'shared.bills'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/bill-list-page/bill-list-page.component').then(
                m => m.BillListPageComponent
              )
          },
          {
            path: 'detail/:bill-id',
            loadComponent: () =>
              import('./pages/bill-detail-page/bill-detail-page.component').then(
                m => m.BillDetailPageComponent
              ),
            canActivate: [detailGuard('bill-id')],
            resolve: { crud: crudResolver('bill-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.bill'
            }
          },
          {
            path: ':bill-id',
            loadComponent: () =>
              import('./pages/create-update-bill-form-page/create-update-bill-form-page.component').then(
                m => m.CreateUpdateBillFormPageComponent
              ),
            canActivate: [crudGuard('bill-id')],
            resolve: {
              crud: crudResolver('bill-id'),
              breadcrumb: breadcrumbResolver('bill-id')
            },
            data: {
              breadcrumbParamValue: 'shared.bill'
            }
          }
        ]
      },
      {
        path: 'solidarity-contribution-bills',
        data: {
          breadcrumb: 'shared.solidarity_contribution_bills'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/solidarity-contribution-bill-list-page/solidarity-contribution-bill-list-page.component').then(
                m => m.SolidarityContributionBillListPageComponent
              )
          },
          {
            path: 'detail/:solidarity-contribution-bill-id',
            loadComponent: () =>
              import('./pages/solidarity-contribution-bill-detail-page/solidarity-contribution-bill-detail-page.component').then(
                m => m.SolidarityContributionBillDetailPageComponent
              ),
            canActivate: [detailGuard('solidarity-contribution-bill-id')],
            resolve: { crud: crudResolver('solidarity-contribution-bill-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.solidarity_contribution_bill'
            }
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./pages/solidarity-contribution-calculation-page/solidarity-contribution-calculation-page.component').then(
                m => m.SolidarityContributionCalculationPageComponent
              ),
            data: {
              breadcrumb: 'new'
            }
          },
          {
            path: ':bill-id',
            redirectTo: '/solidarity-contribution-bills',
            pathMatch: 'full'
          }
        ]
      },
      {
        path: 'camt-import',
        data: {
          breadcrumb: 'finance.camt_import'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/camt-import-list-page/camt-import-list-page.component').then(
                m => m.CamtImportListPageComponent
              )
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./pages/create-camt-import-form-page/create-camt-import-form-page.component').then(
                m => m.CreateCamtImportFormPageComponent
              ),
            data: {
              breadcrumb: 'add_value',
              breadcrumbParamValue: 'finance.camt_import'
            }
          },
          {
            path: ':camt-import-id',
            loadComponent: () =>
              import('./pages/camt-import-page/camt-import-page.component').then(
                m => m.CamtImportPageComponent
              )
          }
        ]
      },
      {
        path: 'period',
        data: {
          breadcrumb: 'finance.accounting_period'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/period-page/period-page.component').then(m => m.PeriodPageComponent)
          }
        ]
      },
      {
        path: 'periods',
        data: {
          breadcrumb: 'finance.accounting_periods'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/period-list-page/period-list-page.component').then(
                m => m.PeriodListPageComponent
              )
          },
          {
            path: 'finalize',
            data: {
              breadcrumb: 'shared.finalize'
            },
            loadComponent: () =>
              import('./pages/finalize-period-page/finalize-period-page.component').then(
                m => m.FinalizePeriodPageComponent
              )
          }
        ]
      },
      {
        path: 'tax-office',
        data: {
          breadcrumb: 'finance.tax_office'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/tax-office-page/tax-office-page.component').then(
                m => m.TaxOfficePageComponent
              )
          }
        ]
      },
      /******************
       * COMMUNITIES
       ******************/
      {
        path: 'communities',
        data: {
          breadcrumb: 'shared.communities'
        },
        children: [
          {
            path: '',
            redirectTo: 'all',
            pathMatch: 'full'
          },
          {
            path: 'all',
            loadComponent: () =>
              import('./pages/community-list-page/community-list-page.component').then(
                m => m.CommunityListPageComponent
              )
          },
          {
            path: 'detail/:community-id',
            loadComponent: () =>
              import('./pages/community-detail-page/community-detail-page.component').then(
                m => m.CommunityDetailPageComponent
              ),
            canActivate: [detailGuard('community-id')],
            resolve: { crud: crudResolver('community-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.community'
            }
          },
          {
            path: 'religious-communities',
            loadComponent: () =>
              import('./pages/religious-community-list-page/religious-community-list-page.component').then(
                m => m.ReligiousCommunityListPageComponent
              ),
            data: {
              breadcrumb: 'shared.community_type.religious_communities'
            }
          },
          {
            path: 'cultic-communities',
            loadComponent: () =>
              import('./pages/cultic-community-list-page/cultic-community-list-page.component').then(
                m => m.CulticCommunityListPageComponent
              ),
            data: {
              breadcrumb: 'shared.community_type.cultic_communities'
            }
          },
          {
            path: 'mosque-facilities',
            loadComponent: () =>
              import('./pages/mosque-facility-community-list-page/mosque-facility-community-list-page.component').then(
                m => m.MosqueFacilityCommunityListPageComponent
              ),
            data: {
              breadcrumb: 'shared.community_type.mosque_facilities'
            }
          },
          {
            path: 'mosques',
            loadComponent: () =>
              import('./pages/mosque-community-list-page/mosque-community-list-page.component').then(
                m => m.MosqueCommunityListPageComponent
              ),
            data: {
              breadcrumb: 'shared.community_type.mosques'
            }
          },
          {
            path: 'professional-associations',
            loadComponent: () =>
              import('./pages/professional-association-community-list-page/professional-association-community-list-page.component').then(
                m => m.ProfessionalAssociationCommunityListPageComponent
              ),
            data: {
              breadcrumb: 'shared.community_type.professional_associations'
            }
          },
          {
            path: 'prayer-rooms',
            loadComponent: () =>
              import('./pages/prayer-room-list/prayer-room-list.component').then(
                m => m.PrayerRoomListComponent
              ),
            data: {
              breadcrumb: 'shared.community_type.prayer_rooms'
            }
          },
          {
            path: ':community-id',
            loadComponent: () =>
              import('~shared/community/pages/create-community-data-form-page/create-community-data-form-page.component').then(
                m => m.CreateCommunityDataFormPageComponent
              ),
            canActivate: [crudGuard('community-id')],
            resolve: {
              crud: crudResolver('community-id'),
              breadcrumb: breadcrumbResolver('community-id')
            },
            data: {
              breadcrumbParamValue: 'shared.community'
            }
          }
        ]
      },
      /******************
       * ACTORS
       ******************/
      {
        path: 'actors',
        data: {
          breadcrumb: 'shared.actors'
        },
        children: [
          {
            path: 'people',
            data: {
              breadcrumb: 'shared.people'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/person-list-page/person-list-page.component').then(
                    m => m.PersonListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                loadComponent: () =>
                  import('./pages/person-detail-page/person-detail-page.component').then(
                    m => m.PersonDetailPageComponent
                  ),
                canActivate: [detailGuard('person-id')],
                resolve: { crud: crudResolver('person-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.person'
                }
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('~shared/core/pages/create-person-data-form-page/create-person-data-form-page.component').then(
                    m => m.CreatePersonDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.person'
                }
              }
            ]
          },
          {
            path: 'members',
            data: {
              breadcrumb: 'shared.role.members'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/member-list-page/member-list-page.component').then(
                    m => m.MemberListPageComponent
                  )
              },
              {
                path: 'detail/:member-id',
                redirectTo: '/actors/people/detail/:member-id'
              },
              {
                path: ':member-id',
                loadComponent: () =>
                  import('./pages/create-member-data-form-page/create-member-data-form-page.component').then(
                    m => m.CreateMemberDataFormPageComponent
                  ),
                canActivate: [crudGuard('member-id')],
                resolve: {
                  crud: crudResolver('member-id'),
                  breadcrumb: breadcrumbResolver('member-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.member'
                }
              }
            ]
          },
          {
            path: 'teachers',
            data: {
              breadcrumb: 'shared.role.teachers'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/teacher-list-page/teacher-list-page.component').then(
                    m => m.TeacherListPageComponent
                  )
              },
              {
                path: 'detail/:teacher-id',
                redirectTo: '/actors/people/detail/:teacher-id'
              },
              {
                path: ':teacher-id',
                redirectTo: '/actors/people/:teacher-id'
              }
            ]
          },
          {
            path: 'inspectors',
            data: {
              breadcrumb: 'shared.role.inspectors'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/inspector-list-page/inspector-list-page.component').then(
                    m => m.InspectorListPageComponent
                  )
              },
              {
                path: 'detail/:inspector-id',
                redirectTo: '/actors/people/detail/:inspector-id'
              },
              {
                path: ':inspector-id',
                redirectTo: '/actors/people/:inspector-id'
              }
            ]
          },
          {
            path: 'accountants',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'shared.role.accountants',
              permissions: {
                only: [RoleEnum.MODERATOR],
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/accountant-list-page/accountant-list-page.component').then(
                    m => m.AccountantListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                redirectTo: '/actors/people/detail/:person-id'
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('./pages/create-accountant-data-form-page/create-accountant-data-form-page.component').then(
                    m => m.CreateAccountantDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.accountant'
                }
              }
            ]
          }
        ]
      },
      /******************
       * ADMINISTRATION
       ******************/
      {
        path: 'administration',
        data: {
          breadcrumb: 'shared.administration'
        },
        children: [
          {
            path: 'finance',
            data: {
              breadcrumb: 'shared.finance'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/finance-administration-page/finance-administration-page.component').then(
                    m => m.FinanceAdministrationPageComponent
                  )
              }
            ]
          },
          {
            path: 'actors',
            data: {
              breadcrumb: 'shared.actors'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/actor-administration-page/actor-administration-page.component').then(
                    m => m.ActorAdministrationPageComponent
                  )
              },
              {
                path: 'member-status',
                data: {
                  breadcrumb: 'shared.member_status'
                },
                children: [
                  {
                    path: ':member-status-id',
                    loadComponent: () =>
                      import('./pages/create-update-member-status-form-page/create-update-member-status-form-page.component').then(
                        m => m.CreateUpdateMemberStatusFormPageComponent
                      ),
                    canActivate: [crudGuard('member-status-id')],
                    resolve: {
                      crud: crudResolver('member-status-id'),
                      breadcrumb: breadcrumbResolver('member-status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'shared.member_status'
                    }
                  }
                ]
              }
            ]
          }
        ]
      },
      /******************
       * PRINT TEMPLATES
       ******************/
      {
        path: 'print-templates',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.print_templates',
          permissions: {
            only: AuthorizationUtil.GROUP_ACCOUNTANT,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('~shared/print/pages/print-management-page/print-management-page.component').then(
                m => m.PrintManagementPageComponent
              )
          },
          {
            path: ':print-template-id',
            loadComponent: () =>
              import('~shared/print/pages/create-update-print-template-form-page/create-update-print-template-form-page.component').then(
                m => m.CreateUpdatePrintTemplateFormPageComponent
              ),
            canActivate: [crudGuard('print-template-id')],
            resolve: {
              crud: crudResolver('print-template-id'),
              breadcrumb: breadcrumbResolver('print-template-id')
            },
            data: {
              breadcrumbParamValue: 'shared.print_template'
            }
          }
        ]
      },
      /******************
       * NOT FOUND
       ******************/
      {
        path: 'not-found',
        loadComponent: () =>
          import('~shared/shared/pages/page-not-found-page/page-not-found-page.component').then(
            m => m.PageNotFoundPageComponent
          ),
        data: {
          breadcrumb: 'page_not_found'
        }
      },
      { path: '**', redirectTo: 'not-found' }
    ]
  }
];
